import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, TextInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Wallet, Plus, Search, Trash2, Lock, TrendingUp } from "lucide-react";
import { fmtINR, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const STATUS_TONE = {
    DRAFT: { tone: "slate", label: "Draft" },
    DIRECTOR_REVIEW: { tone: "amber", label: "Director Review" },
    DIRECTOR_APPROVED: { tone: "emerald", label: "Locked" },
    HELD_BY_DIRECTOR: { tone: "red", label: "Held" },
};

export default function SettlementList() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [filterCust, setFilterCust] = useState("ALL");
    const [filterStatus, setFilterStatus] = useState("ALL");
    const canCreate = hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING");
    const canDelete = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        Promise.all([api.get("/settlements"), api.get("/customers")])
            .then(([s, c]) => { setRows(s.data); setCustomers(c.data); })
            .catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (!rows) return null;
        return rows.filter((r) => {
            if (search && !(r.reference?.toLowerCase().includes(search.toLowerCase()))) return false;
            if (filterCust !== "ALL" && r.customer_id !== filterCust) return false;
            if (filterStatus !== "ALL" && r.status !== filterStatus) return false;
            return true;
        });
    }, [rows, search, filterCust, filterStatus]);

    const remove = async (r) => {
        if (!window.confirm(`Delete ${r.reference}?`)) return;
        try { await api.delete(`/settlements/${r.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid="settlement-list-page">
            <PageHeader
                eyebrow="MODULE · STEP 4 OF 4"
                title="Settlements · Quarterly Negotiations"
                subtitle="Track steel-rate revisions, calculate DMC impact, get Intelligent Advisor recommendations, and lock with Director sign-off."
                actions={
                    <div className="flex items-center gap-2">
                        <SlateButton onClick={() => navigate("/steel-price-history")} data-testid="sett-price-history-btn">
                            <TrendingUp className="h-4 w-4" /> SIAM History
                        </SlateButton>
                        {canCreate && (
                            <CopperButton onClick={() => navigate("/settlement/new")} data-testid="sett-new-btn">
                                <Plus className="h-4 w-4" /> New Settlement
                            </CopperButton>
                        )}
                    </div>
                }
            />

            <Card className="p-3 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="relative col-span-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        <TextInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by reference…"
                            className="pl-8 h-8 text-[12px]"
                            data-testid="sett-search-input"
                        />
                    </div>
                    <Select value={filterCust} onChange={(e) => setFilterCust(e.target.value)} className="h-8 text-[12px]" data-testid="sett-filter-customer">
                        <option value="ALL">All customers</option>
                        {customers.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                    </Select>
                    <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-8 text-[12px]" data-testid="sett-filter-status">
                        <option value="ALL">All statuses</option>
                        {Object.entries(STATUS_TONE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </Select>
                </div>
            </Card>

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}
            {filtered && filtered.length === 0 && (
                <EmptyState
                    icon={Wallet}
                    title="No settlements yet"
                    description={canCreate ? "Create a quarterly settlement to start tracking steel-rate revisions." : "Awaiting Finance to file a settlement."}
                    action={canCreate && <CopperButton onClick={() => navigate("/settlement/new")}><Plus className="h-4 w-4" /> Create First Settlement</CopperButton>}
                />
            )}

            {filtered && filtered.length > 0 && (
                <Card>
                    <table className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-3">Reference</th>
                                <th className="eyebrow text-left py-2 px-3">Customer</th>
                                <th className="eyebrow text-left py-2 px-3">Type</th>
                                <th className="eyebrow text-left py-2 px-3">Period</th>
                                <th className="eyebrow text-right py-2 px-3">DMC Impact</th>
                                <th className="eyebrow text-right py-2 px-3">Total Claim</th>
                                <th className="eyebrow text-left py-2 px-3">Status</th>
                                <th className="eyebrow text-right py-2 px-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const st = STATUS_TONE[r.status] || { tone: "slate", label: r.status };
                                const c = r.computed || {};
                                const locked = r.locked === true;
                                return (
                                    <tr key={r.id} className="border-b border-slate-100" data-testid={`sett-row-${r.reference}`}>
                                        <td className="py-1.5 px-3 font-mono font-semibold text-[13px]">
                                            <div className="flex items-center gap-2">
                                                <Link to={`/settlement/${r.id}`} className="hover:text-[hsl(var(--copper))]">{r.reference}</Link>
                                                {locked && <Lock className="h-3 w-3 text-emerald-600" />}
                                            </div>
                                        </td>
                                        <td className="py-1.5 px-3 text-[13px]">{r.customer_code}</td>
                                        <td className="py-1.5 px-3 text-[12px] font-mono">{r.settlement_type}</td>
                                        <td className="py-1.5 px-3 text-[11px] font-mono text-slate-500">
                                            {fmtDate(r.period_from)} <span className="text-slate-300">→</span> {fmtDate(r.period_to)}
                                        </td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px]">{fmtINR(c.dmc_full_period || 0)}</td>
                                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px] font-semibold">{fmtINR(c.total_claim || 0)}</td>
                                        <td className="py-1.5 px-3"><Pill tone={st.tone}>{st.label}</Pill></td>
                                        <td className="py-1.5 px-3 text-right">
                                            <div className="inline-flex gap-1">
                                                <SlateButton onClick={() => navigate(`/settlement/${r.id}`)} variant="ghost" className="h-7 px-2 text-[11px]">View</SlateButton>
                                                {canCreate && !locked && r.status === "DRAFT" && (
                                                    <SlateButton onClick={() => navigate(`/settlement/${r.id}/edit`)} variant="ghost" className="h-7 px-2 text-[11px]">Edit</SlateButton>
                                                )}
                                                {canDelete && !locked && (
                                                    <SlateButton onClick={() => remove(r)} variant="ghost" className="h-7 w-7 px-0 text-red-600 hover:bg-red-50" title="Delete">
                                                        <Trash2 className="h-3 w-3" />
                                                    </SlateButton>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}
